import React, { useEffect, useState } from "react";
import { FullScreenModal, ModalAnimationStyle } from "@communicare/medications/Components/FullScreenModal";
import { Box, Checkbox, FormControlLabel, FormGroup, Grid, Table, TableBody, TableCell } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { Column, Columns, Text} from "@dna/react";
import { ContentRow } from "../pharmacy-medications-table/ContentRow";
import { Button as CButton, ButtonVariant } from "@communicare/medications/Components/Button";
import { HeaderCell } from "../../common/material-table";
import {
    MedicationState,
    ProductStatus
} from "../../../services/medicationmanagement/medications";
import { DangerToolTipContent } from "../../common/tool-tip/DangerToolTipContent";
import { EmedStyle } from "../../common/style/emed.style";
import {useParams, useRouteMatch} from "react-router-dom";
import { QueryParams } from "../../common/constants";
import { PharmacyMedicationsLoader } from "../PharmacyMedicationsLoader";
import {PharmacyErrors, SelectableMedication, usePharmacyMedicationsLoader} from "../usePharmacyMedications";
import { ConfirmLoading } from "./ConfirmLoading";
import { useAsync } from "../../common/async";
import { patientService } from "../../../services/medicationmanagement/patientApi/PatientApi";
import { allergyExclusionDisplayText } from "../../resident-header/ResidentAllergyUtils";
import { PatientAllergyHistory } from "../../../services/medicationmanagement/patientApi/models/PatientAllergyHistory";
import { PatientAllergyStatus } from "../../../services/medicationmanagement/patientApi/models/PatientAllergyStatus";
import { RenderIf } from "../../common/render-if/RenderIf";
let iconpack = require("@dna/icons");

const useStyles = makeStyles({
    tableContainer: {
        margin: "0 24px 24px 24px",
        width: "calc(100vw - 48px)",
        backgroundColor: "rgba(255, 255, 255, 0.5)"
    },
    allergyUpdate: {
        backgroundColor: "#FBF1F1",
        borderColor: "#C53535",
        borderLeftColor: "#C53535",
        borderLeftWidth: "4px",
        borderWidth: "1px",
        borderRadius: "2px",
        borderStyle: "solid",
        padding: 17,
        color: EmedStyle.EmedBadge.critical
    },
    checkBoxLabel: {
        fontWeight: 600,
        lineHeight: "20px"
    }
})

export const ConfirmMedicationChange = (props: { onClose: () => void }) => {
    const classes = useStyles();
    let params: any = useParams();
    let { path } = useRouteMatch();
    let authoringSystem = params[QueryParams.AuthoringSystem];
    let authoringSystemForeignKey = params[QueryParams.AuthoringSystemForeignKey];
    let providedMedicationId = params[QueryParams.PharmacyMedicationOrderId];
    let getAllergyInfo = useAsync();
    let { loading, error, data, confirmingInProgress, confirm, toggleMedicationState, selectAll, unSelectAll } = usePharmacyMedicationsLoader(authoringSystem, authoringSystemForeignKey,providedMedicationId);

    useEffect(() => {
        getAllergyInfo.queryDispatcher(patientService.getPatientAllergyInfo(authoringSystem, authoringSystemForeignKey));
    }, [authoringSystem, authoringSystemForeignKey]);

    let medicationData = data.meds;
    let medicationId = data.medicationId;

    let [actioned, setActioned] = useState(false);

    let newDisclaimer = allergyExclusionDisplayText(getAllergyInfo.success?.allergyHistory?.newDisclaimer);
    let disclaimer = allergyExclusionDisplayText(getAllergyInfo.success?.allergyHistory?.disclaimer);

    let allergyHistory: PatientAllergyHistory = getAllergyInfo.success?.allergyHistory;
    let previouscontent = allergyHistory?.allergyHistoryDetails?.map(function (el: any) {
        return el.allergen + `(` + el.reaction + `)`;
    }).join(',');

    let currentcontent = allergyHistory?.newAllergyHistoryDetails?.map(function (el: any) {
        return el.allergen + `(` + el.reaction + `)`;
    }).join(',');

    let allergyContent =
        allergyHistory?.status !== PatientAllergyStatus.Confirmed &&
            (allergyHistory?.newNote || allergyHistory?.newAllergyHistoryDetails || allergyHistory?.newDisclaimer)
            ?
            {
                previoustitle: "PREVIOUS allergies (and adverse reactions)", //props.patient.allergyHistory?.note,
                previousnote: getAllergyInfo.success.allergyHistory?.note,
                previousdisclaimer: disclaimer,
                previouscontent: previouscontent?.replace(/,\s*$/, ""),
                currenttitle: "new allergies (and adverse reactions)",
                currentnote: getAllergyInfo.success.allergyHistory?.newNote,
                currentdisclaimer: newDisclaimer,
                currentcontent: currentcontent?.replace(/,\s*$/, ""),
                status: getAllergyInfo.success.allergyHistory?.status
            } : null;

    const getActionableMeds = (medications: SelectableMedication[] | null | undefined) => {
        if (medications) {
            let meds = medications.filter(x=>!x.confirmed).filter(x => x.status === MedicationState.New || x.status === MedicationState.RePrescribed || x.status === MedicationState.Ceased);
            return {
                meds,
                total: meds.length,
                selected: meds.filter(x => x.product?.action === ProductStatus.Actioned).length
            };
        }
        return {
            meds: [],
            total: 0,
            selected: 0
        };
    }

    let rows = getActionableMeds(medicationData);

    const canConfirmMeds = (total: number, selected: number, allergyActioned: boolean) => {
        if (!allergyContent) {
            return total > 0 && total === selected;
        }

        return total === selected && allergyActioned;

    };

    const onAllergyActioned = () => {
        let newStatus = !actioned;
        patientService
            .saveAllergyAction(allergyHistory.id, newStatus, authoringSystem, authoringSystemForeignKey)
            .then(_ => {
                setActioned(newStatus);
            }).catch(_ => setActioned(actioned));

    }

    let loadButtons = (isLoading: boolean, canConfirm: boolean) => {
        if (isLoading) {
            return [];
        }
        return [
            <Grid container direction="column" spacing={3}>

                <RenderIf if={providedMedicationId !== medicationId}>
                    <Grid item xs>
                        <Columns
                            css={{
                                gap: "small",
                                backgroundColor: "caution-lightest",
                                borderColor: "caution-dark",
                                borderEndWidth: "thin",
                                borderTopWidth: "thin",
                                borderBottomWidth: "thin",
                            }}
                        >
                            <Column
                                width="content"
                                css={{
                                    color: "caution-dark",
                                    paddingTop: "small",
                                    paddingStart: "small",
                                    borderStartColor: "caution-dark",
                                    borderBottomStartRadius: "small",
                                    borderStartWidth: "thicker",
                                }}
                            >
                                <iconpack.IconCaution level="medium" />
                            </Column>

                            <Column
                                css={{
                                    minHeight: 70,
                                    paddingTop: "small",
                                }}
                            >
                                <div style={{ color: '#925D07', fontSize: "10px" }}>
                                    <Text
                                        level="xsmall"
                                        as="strong"
                                    >
                                        Warning!
                                    </Text></div>
                                <div style={{ color: '#925D07' }}>
                                    <Text level="small"
                                    >
                                        A new pharmacy request has been generated since you began actioning medication/allergy changes.
                                    </Text></div>
                                <div style={{ color: '#925D07' }}>
                                    <Text level="small">
                                        Please review and action the changes (highlighted in the list above) before you proceed.
                                    </Text></div>
                            </Column>
                        </Columns>
                    </Grid>
                </RenderIf>

                <Grid item xs>
                    {error && (error===PharmacyErrors.MedicationActionError || error===PharmacyErrors.MedicationConfirmationError)? <div style={{ marginBottom: "30px", marginTop: "20px" }}>
                        <Columns
                            css={{
                                gap: "small",
                                backgroundColor: "caution-lightest",
                                borderColor: "caution-dark",
                                borderStartWidth: "thicker",
                                borderEndWidth: "thin",
                                borderTopWidth: "thin",
                                borderBottomWidth: "thin",
                            }}
                        >
                            <Column
                                width="content"
                                css={{
                                    color: "caution-dark",
                                    paddingTop: "small",
                                    paddingStart: "small",
                                    borderBottomStartRadius: "small",
                                }}
                            >
                                <iconpack.IconCaution level="medium" />
                            </Column>
                            <Column
                                css={{
                                    minHeight: 70,
                                    paddingTop: "small",
                                    color: "caution-dark",
                                }}
                            >
                                <div style={{ color: '#925D07', fontSize: "10px" }}>
                                    <Text
                                        level="xsmall"
                                        as="strong"
                                    >
                                        There was a problem marking this change as actioned
                                        </Text></div>
                                <div style={{ color: '#925D07' }}>
                                    <Text level="small"
                                    >
                                        We have experienced an issue confirming the medication/allergy changes have been actioned.
                                        </Text></div>
                                <div style={{ color: '#925D07' }}>
                                    <Text level="small">
                                        Please try again.
                                        </Text></div>
                            </Column>
                        </Columns></div> : <></>}
                </Grid>

                <Grid item xs>
                    <Grid container spacing={3}>
                        <Grid item xs={8}>
                            {!allergyContent ? null :
                                <Box className={classes.allergyUpdate}>
                                    <FormGroup row>
                                        <FormControlLabel classes={{
                                            label: classes.checkBoxLabel
                                        }}
                                            control={<Checkbox
                                                checked={actioned}
                                                disableRipple
                                                onChange={() => {
                                                    onAllergyActioned();
                                                }}
                                                color="primary"
                                                inputProps={{ 'aria-label': 'allergy changes actioned' }}
                                            />}
                                            label="Allergy changes actioned"
                                        />
                                    </FormGroup>

                                    <DangerToolTipContent
                                        {...allergyContent}
                                    />
                                </Box>}
                        </Grid>
                        <Grid item xs={4}>
                            <div style={{ float: "right" }}>
                                <CButton
                                    key='confirm'
                                    width={168}
                                    type='button'
                                    variant={ButtonVariant.primary}
                                    disabled={!canConfirm}
                                    testId={'navigation-control-Continue'}
                                    onClick={e => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        confirm().then(() => {
                                            props.onClose()
                                        })
                                    }}
                                >
                                    Confirm
                            </CButton>
                            </div>
                        </Grid>
                    </Grid>
                </Grid>

            </Grid>
        ]
    };

    const handleSelectAllClicked = (evnt: React.ChangeEvent<HTMLInputElement>) => {
        if (evnt.target.checked) {
            selectAll();
        }
        unSelectAll();
    };
    
    return (
        <FullScreenModal title={
            rows.total > 0 ?
                "Confirm medication/allergy changes have been actioned" : ""
        }
            info={
                rows.total > 0 ?
                    <>
                        <Box>Select the medication and/or allergy you want to confirm as being actioned for this resident.</Box>
                        <Box>Once confirmed as actioned the medication and/or allergy status will be reset.</Box>
                    </> : <></>
            }
            modalAnimationStyle={ModalAnimationStyle.None}
            onClose={props.onClose}
            buttons={
                loadButtons((loading || getAllergyInfo.inProgress) || (!!confirmingInProgress)
                    , canConfirmMeds(rows.total, rows.selected, actioned))
            }
            nonInteractivePlaceholder={confirmingInProgress ? <ConfirmLoading /> : null}
        >
            <PharmacyMedicationsLoader inProgress={loading || getAllergyInfo.inProgress} error={error && error === PharmacyErrors.MedicationLoadingError} empty={rows.total <= 0}>
                <TableContainer
                    className={classes.tableContainer}
                >
                    <Table>
                        <TableHead>
                            <TableRow role="header">
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        indeterminate={rows.selected > 0 && rows.selected < rows.total}
                                        checked={rows.total > 0 && rows.selected === rows.total}
                                        color={"primary"}
                                        onChange={handleSelectAllClicked}
                                        inputProps={{ 'aria-label': 'select all desserts' }}
                                    />
                                </TableCell>
                                <HeaderCell key={1}>
                                    Product
                            </HeaderCell>
                                <HeaderCell key={2}>
                                    Course instructions
                            </HeaderCell>
                                <HeaderCell key={3}>
                                    Course script no.
                            </HeaderCell>
                                <HeaderCell key={4}>
                                    Course start
                            </HeaderCell>
                                <HeaderCell key={5}>
                                    Course stop
                            </HeaderCell>
                                <HeaderCell key={6}>
                                    Prescribed by
                            </HeaderCell>
                                <HeaderCell key={7}>
                                    Status
                            </HeaderCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.meds.map((row: SelectableMedication) => {
                                return <ContentRow
                                    key={`${row.authoringSystem}_${row.authoringSystemForeignKey}`}
                                    isSelectable={true}
                                    item={row}
                                    onActioned={toggleMedicationState}
                                />
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </PharmacyMedicationsLoader>
        </FullScreenModal>
    );
}